import React, { useContext, useEffect, useReducer, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Button from 'react-bootstrap/Button';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    case 'UPLOAD_REQUEST':
      return { ...state, loadingUpload: true, errorUpload: '' };
    case 'UPLOAD_SUCCESS':
      return { ...state, loadingUpload: false, errorUpload: '' };
    case 'UPLOAD_FAIL':
      return { ...state, loadingUpload: false, errorUpload: action.payload };
    default:
      return state;
  }
};

export default function ProductEditScreen() {
  const params = useParams(); // /product/:id
  const { id: productId } = params;

  const { state } = useContext(Store);
  const { userInfo } = state;
  const [{ loading, error, loadingUpdate, loadingUpload }, dispatch] =
    useReducer(reducer, {
      loading: true,
      error: '',
    });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [price, setPrice] = useState(0);
  const [images, setImages] = useState([]);
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [sizes, setSizes] = useState([{ size: 'S', countInStock: 0 }]); // Default sizes
  const [countInStock, setCountInStock] = useState(0);
  const [salePrice, setSalePrice] = useState('');
  const [colors, setColors] = useState([]);
  const [activeColorUploadIndex, setActiveColorUploadIndex] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await axios.get(`/api/products/${productId}`);
        setName(data.name);
        setSlug(data.slug);
        setPrice(data.price);
        setImages(data.image);
        setCategory(data.category);
        setBrand(data.brand);
        setDescription(data.description);
        setSizes(data.sizes || [{ size: 'S', countInStock: 0 }]); // Handle sizes
        setCountInStock(data.countInStock);
        setSalePrice(data.salePrice != null ? data.salePrice : '');
        setColors(data.colors || []);
        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [productId]);

  const fileInputRef = useRef(null);
  const colorFileInputRef = useRef(null);

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };



  // Handle changes to individual image URLs
  const handleImageChange = (index, value) => {
    const updatedImages = [...images];
    updatedImages[index] = value;
    setImages(updatedImages);
  };

  // Remove an image URL from the list
  const removeImage = (index) => {
    setImages(images.filter((_, idx) => idx !== index));
  };

  // Function to handle input changes for sizes
  const handleSizeChange = (index, field, value) => {
    const updatedSizes = [...sizes];
    updatedSizes[index][field] = value;
    setSizes(updatedSizes);
  };

  // Function to add a new size entry
  const addSize = () => {
    setSizes([...sizes, { size: 'XS', countInStock: 0 }]); // Default size XS
  };

  // Function to remove a size entry
  const removeSize = (index) => {
    const updatedSizes = sizes.filter((_, i) => i !== index);
    setSizes(updatedSizes);
  };

  // Color handlers
  const handleColorNameChange = (index, value) => {
    const updated = [...colors];
    updated[index] = { ...updated[index], name: value };
    setColors(updated);
  };

  const handleColorImageChange = (colorIndex, imageIndex, value) => {
    const updated = [...colors];
    updated[colorIndex].images[imageIndex] = value;
    setColors(updated);
  };

  const removeColorImage = (colorIndex, imageIndex) => {
    const updated = [...colors];
    updated[colorIndex] = {
      ...updated[colorIndex],
      images: updated[colorIndex].images.filter((_, i) => i !== imageIndex),
    };
    setColors(updated);
  };

  const addColor = () => {
    setColors([...colors, { name: '', images: [] }]);
  };

  const removeColor = (index) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  const handleColorUploadClick = (colorIndex) => {
    setActiveColorUploadIndex(colorIndex);
    if (colorFileInputRef.current) {
      colorFileInputRef.current.click();
    }
  };

  const uploadColorImageHandler = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const bodyFormData = new FormData();
    bodyFormData.append('file', file);
    try {
      dispatch({ type: 'UPLOAD_REQUEST' });
      const { data } = await axios.post('/api/upload', bodyFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      dispatch({ type: 'UPLOAD_SUCCESS' });
      toast.success('Image uploaded successfully');
      const updated = [...colors];
      updated[activeColorUploadIndex] = {
        ...updated[activeColorUploadIndex],
        images: [...updated[activeColorUploadIndex].images, data.secure_url],
      };
      setColors(updated);
      e.target.value = '';
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPLOAD_FAIL', payload: getError(err) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `/api/products/${productId}`,
        {
          name,
          slug,
          price: Number(price),
          salePrice: salePrice !== '' ? Number(salePrice) : null,
          image: images,
          colors,
          category,
          brand,
          description,
          sizes,
          countInStock,
        },
        {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        },
      );
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Product updated successfully');
      navigate('/admin/products');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  const uploadFileHandler = async (e) => {
    const file = e.target.files[0];

    const bodyFormData = new FormData();

    bodyFormData.append('file', file);

    try {
      dispatch({ type: 'UPLOAD_REQUEST' });

      const { data } = await axios.post('/api/upload', bodyFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',

          authorization: `Bearer ${userInfo.token}`,
        },
      });

      dispatch({ type: 'UPLOAD_SUCCESS' });

      toast.success('Image uploaded successfully');

      setImages([...images, data.secure_url]);
    } catch (err) {
      toast.error(getError(err));

      dispatch({ type: 'UPLOAD_FAIL', payload: getError(err) });
    }
  };

  return (
    <Container className="small-container">
      <Helmet>
        <title>Edit Product {productId}</title>
      </Helmet>
      <h1>Edit Product {productId}</h1>

      {loading ? (
        <LoadingBox></LoadingBox>
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="slug">
            <Form.Label>Slug</Form.Label>
            <Form.Control
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="price">
            <Form.Label>Price (EGP)</Form.Label>
            <Form.Control
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="salePrice">
            <Form.Label>
              Sale Price (EGP){' '}
              <small className="text-muted">— optional, leave empty for no sale</small>
            </Form.Label>
            <Form.Control
              type="number"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              placeholder="Leave empty if not on sale"
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="images">
            <Form.Label>Images</Form.Label>
            {images.map((image, index) => (
              <div key={index} className="d-flex mb-2">
                <Form.Control
                  type="text"
                  value={image}
                  onChange={(e) => handleImageChange(index, e.target.value)}
                  placeholder="Enter image URL"
                  required
                />
                <Button
                  variant="danger"
                  onClick={() => removeImage(index)}
                  className="ms-2"
                >
                  Remove
                </Button>
              </div>
            ))}
            <div className="mt-2">
              <Button type="button" onClick={handleUploadButtonClick} disabled={loadingUpload}>
                Upload Image
              </Button>
              <Form.Control
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={uploadFileHandler}
              />
              {/* Hidden file input for color images */}
              <Form.Control
                type="file"
                ref={colorFileInputRef}
                style={{ display: 'none' }}
                onChange={uploadColorImageHandler}
              />
              {loadingUpload && <LoadingBox></LoadingBox>}
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="category">
            <Form.Label>Category</Form.Label>
            <Form.Control
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="brand">
            <Form.Label>Brand</Form.Label>
            <Form.Control
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="countInStock">
            <Form.Label>countInStock</Form.Label>
            <Form.Control
              value={countInStock}
              onChange={(e) => setCountInStock(e.target.value)}
              required
            />
          </Form.Group>

          {/* Sizes */}
          <Form.Group className="mb-3" controlId="sizes">
            <Form.Label>Sizes</Form.Label>
            {sizes.map((sizeItem, index) => (
              <div key={index} className="d-flex mb-2">
                <Form.Select
                  value={sizeItem.size}
                  onChange={(e) =>
                    handleSizeChange(index, 'size', e.target.value)
                  }
                  className="me-2"
                  required
                >
                  <option value="age 2-3">age 2-3</option>
                  <option value="age 3-4">age 3-4</option>
                  <option value="age 4-5">age 4-5</option>
                  <option value="age 5-6">age 5-6</option>
                  <option value="age 6-7">age 6-7</option>
                  <option value="age 7-8">age 7-8</option>
                  <option value="age 8-9">age 8-9</option>
                  <option value="age 9-10">age 9-10</option>
                  <option value="age 10-11">age 10-11</option>
                  <option value="age 11-12">age 11-12</option>
                  <option value="age 12-13">age 12-13</option>
                  <option value="age 13-14">age 13-14</option>
                  <option value="age 14-15">age 14-15</option>
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                  <option value="One Size">One Size</option>
                </Form.Select>
                <Form.Control
                  type="number"
                  value={sizeItem.countInStock}
                  onChange={(e) =>
                    handleSizeChange(index, 'countInStock', e.target.value)
                  }
                  required
                  placeholder="Stock"
                  className="me-2"
                />
                <Form.Control
                  type="number"
                  value={sizeItem.price ?? ''}
                  onChange={(e) =>
                    handleSizeChange(
                      index,
                      'price',
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                  placeholder="Price override (optional)"
                  className="me-2"
                />
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => removeSize(index)}
                  className="ms-2"
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="button" onClick={addSize} className="mt-2">
              + Add Size
            </Button>
          </Form.Group>

          {/* Colors */}
          <Form.Group className="mb-3" controlId="colors">
            <Form.Label>Color Variants</Form.Label>
            {colors.map((colorItem, colorIndex) => (
              <div
                key={colorIndex}
                className="border rounded p-3 mb-3"
              >
                <div className="d-flex align-items-center mb-2">
                  <Form.Control
                    type="text"
                    value={colorItem.name}
                    onChange={(e) =>
                      handleColorNameChange(colorIndex, e.target.value)
                    }
                    placeholder="Color name (e.g. black, red)"
                    className="me-2"
                    required
                  />
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => removeColor(colorIndex)}
                  >
                    Remove Color
                  </Button>
                </div>
                <div className="ms-2">
                  <Form.Label className="text-muted small">Images for this color</Form.Label>
                  {colorItem.images.map((img, imgIndex) => (
                    <div key={imgIndex} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        value={img}
                        onChange={(e) =>
                          handleColorImageChange(colorIndex, imgIndex, e.target.value)
                        }
                        placeholder="Image URL"
                      />
                      <Button
                        type="button"
                        variant="outline-danger"
                        onClick={() => removeColorImage(colorIndex, imgIndex)}
                        className="ms-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleColorUploadClick(colorIndex)}
                    disabled={loadingUpload}
                    className="mt-1"
                  >
                    Upload Image
                  </Button>
                  {loadingUpload && activeColorUploadIndex === colorIndex && (
                    <LoadingBox />
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline-primary" onClick={addColor}>
              + Add Color
            </Button>
          </Form.Group>

          <div className="mb-3">
            <Button disabled={loadingUpdate} type="submit">
              Update
            </Button>
            {loadingUpdate && <LoadingBox></LoadingBox>}
          </div>
        </Form>
      )}
    </Container>
  );
}
